import { useAuth0 } from '@auth0/auth0-react';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { FC, useEffect } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { ENV_CONFIG } from 'config/environment';

const STATSIG_NETWORK_TIMEOUT_MS = 3000;

const getEnvironmentTierFromEnvConfig = (env: string) => {
  if (env === 'PRODUCTION') {
    return 'production';
  }
  if (env === 'LOCALHOST') {
    return 'development';
  }
  // if env is 'STAGING' or 'PREVIEW', use 'staging
  // and use 'staging' as fallback
  return 'staging';
};

interface Props {
  children: React.ReactNode;
}

const IdentifiedStatsig: FC<Props & { userID?: string }> = ({ userID, children }) => {
  const { client } = useClientAsyncInit(
    process.env.REACT_APP_STATSIG_CLIENT_SDK_KEY ?? '',
    { userID },
    {
      environment: { tier: getEnvironmentTierFromEnvConfig(ENV_CONFIG().MODE) },
      networkConfig: { networkTimeoutMs: STATSIG_NETWORK_TIMEOUT_MS },
    }
  );

  useEffect(() => {
    const onFailure = () =>
      datadogLogs.logger.warn('Statsig initialization failed; gates are on defaults', {
        user_id: userID,
      });
    client.on('initialization_failure', onFailure);
    return () => client.off('initialization_failure', onFailure);
  }, [client, userID]);

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

export const Statsig: FC<Props> = ({ children }) => {
  const { isLoading, user } = useAuth0();

  // Wait for Auth0 so Statsig initializes once with the real user, not
  // anonymous-then-reidentified (loses live gate freshness - OATH-4368).
  // Still render children while loading - returning null here would block
  // all routing/data-fetching, not just Statsig.
  if (isLoading) return <>{children}</>;

  return <IdentifiedStatsig userID={user?.sub}>{children}</IdentifiedStatsig>;
};
